import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Modal } from "antd";
import moment from "moment-timezone";

// Components
import ActivityChart from "./ActivityChart";
import LatestNews from "./LatestNews";
import PointsBalance from "./PointsBalance";
import Challenges from "./Challanges";
import GoalsOverview from "./GoalsOverview";
import { MetricsRecord } from "./ActivityChart";
import type { HomeChallenge } from "./Challanges";

// Assets
import GoalsImage from "../assets/Goals.png";

// Utilities
import {
  getHealthMetrics,
  fetchAssignedChallenges,
  useCheckSessionHeartBeat,
} from "components/API/ApiEnpoints";
import { SessionExtend } from "components/utils/SessionCheckUtils";
import { getImageFromCache, saveImageToCache } from "../utils/Imagecacheutils";

const HomePage: React.FC = () => {
  const [healthMetrics, setHealthMetrics] = useState<MetricsRecord[]>([]);
  const [challengesData, setChallengesData] = useState<HomeChallenge[]>([]);
  const [challengesLoading, setChallengesLoading] = useState(true);
  const [orgHealthMetrics, setOrgHealthMetrics] = useState<MetricsRecord[]>([]);
  const [selfHealthMetrics, setSelfHealthMetrics] = useState<MetricsRecord[]>(
    []
  );
  const [activityGoalSource, setActivityGoalSource] = useState<"org" | "self">(
    "org"
  );
  const [overviewGoalSource, setOverviewGoalSource] = useState<"org" | "self">(
    "org"
  );

  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";
  const beAddress =
    window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

  // --- Image Fetching Helper (Ported from ChallangesPage) ---
  const fetchImagesByUid = async (
    imageUids: string[]
  ): Promise<Record<string, { image: string; timestamp: string }>> => {
    if (!imageUids || imageUids.length === 0) {
      return {};
    }

    try {
      const url = new URL(`${beAddress}/api/get-image-by-uid`);
      imageUids.forEach((uid) =>
        url.searchParams.append("image_uid_list", uid)
      );
      url.searchParams.set("is_icon_img", "false");
      url.searchParams.set("is_feedback_image", "false");

      const res = await fetch(url.toString(), {
        headers: {
          "user-uid": userUid,
        },
        credentials: "include",
      });
      const data = await res.json();

      if (data.status === "Success" && Array.isArray(data.data)) {
        return Object.fromEntries(
          data.data.map((item: any) => {
            if (item.default)
              return [item.image_uid, { image: "", timestamp: "" }];
            return [
              item.image_uid,
              { image: item.image, timestamp: item.timstamp },
            ];
          })
        );
      }
    } catch (err) {
      console.error("Failed to fetch images:", err);
    }

    return {};
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const baseParams = {
          from_datetime: moment
            .utc()
            .subtract(7, "days")
            .format("YYYY-MM-DDTHH:mm:ss[Z]"),
          to_datetime: moment.utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
          metric_data_type: "all",
          goal_type: "all",
        };

        const [orgRes, selfRes] = await Promise.all([
          getHealthMetrics(sessionUid, userUid, {
            ...baseParams,
            goal_source: "org",
          }),
          getHealthMetrics(sessionUid, userUid, {
            ...baseParams,
            goal_source: "self",
          }),
        ]);

        if (orgRes.status === "Success") setOrgHealthMetrics(orgRes.data.data);
        if (selfRes.status === "Success")
          setSelfHealthMetrics(selfRes.data.data);
      } catch (error) {
        console.error("Error fetching health metrics:", error);
      }
    };

    const getChallenges = async () => {
      setChallengesLoading(true);
      try {
        const res = await fetchAssignedChallenges(sessionUid, userUid, {});
        if (res.status === "Success") {
          let assigned = Array.isArray(res.data) ? res.data : [];

          // 1. Check Cache for existing images
          const cacheCheckPromises = assigned.map(async (ch: any) => {
            const imageUid = ch.image_details?.image_uid;
            const imageTimestamp = ch.image_details?.timestamp || "";

            if (!imageUid) return { ...ch, needsFetch: false, imageUid: null };

            const cachedUrl = await getImageFromCache(imageUid, imageTimestamp);

            return {
              ...ch,
              imageUid,
              imageTimestamp,
              cachedUrl,
              needsFetch: !cachedUrl,
            };
          });

          const cacheChecked = await Promise.all(cacheCheckPromises);

          // 2. Filter UIDs that need to be fetched from API
          const imageUidsToFetch = cacheChecked
            .filter((ch) => ch.needsFetch)
            .map((ch) => ch.imageUid)
            .filter(Boolean);

          // 3. Bulk fetch missing images
          let fetchedImages: Record<
            string,
            { image: string; timestamp: string }
          > = {};
          if (imageUidsToFetch.length > 0) {
            try {
              fetchedImages = await fetchImagesByUid(imageUidsToFetch);
            } catch (err) {
              console.error("Image fetch failed:", err);
            }
          }

          // 4. Merge Cache, Fetched Data, and Fallbacks
          const finalChallenges = await Promise.all(
            cacheChecked.map(async (challenge: any) => {
              const { imageUid, imageTimestamp, cachedUrl } = challenge;

              // Case A: No UID (Use fallback in UI)
              if (!imageUid) {
                return { ...challenge, image: "" };
              }

              // Case B: Found in Cache
              if (cachedUrl) {
                return { ...challenge, image: cachedUrl };
              }

              // Case C: Fetched from API
              const fetchedImageData = fetchedImages[imageUid];
              if (fetchedImageData && fetchedImageData.image) {
                // Save to cache for next time
                await saveImageToCache(
                  imageUid,
                  imageUid,
                  fetchedImageData.image,
                  fetchedImageData.timestamp
                );

                // Get the Blob URL
                const newUrl = await getImageFromCache(
                  imageUid,
                  fetchedImageData.timestamp
                );
                return { ...challenge, image: newUrl || "" };
              }

              // Case D: Failed to fetch (Use fallback in UI)
              return { ...challenge, image: "" };
            })
          );

          setChallengesData(finalChallenges);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setChallengesLoading(false);
      }
    };

    fetchData();
    getChallenges();
    SessionExtend();
  }, [sessionUid, userUid, beAddress]);

  return (
    <PageContainer>
      <DashboardGrid>
        {/* --- Top Left: Activity Chart --- */}
        <GridItem area="stats">
          <SectionHeader>
            <span>Weekly Activity</span>
            <InfoIcon
              type="tooltip"
              content="This graph provides an overview of the weekly targets compared to the achieved goals"
            />
          </SectionHeader>
          <ActivityChartWrapper>
            <ActivityChart
              orgMetrics={orgHealthMetrics}
              selfMetrics={selfHealthMetrics}
              goalSource={activityGoalSource}
              onGoalToggle={setActivityGoalSource}
            />
          </ActivityChartWrapper>
        </GridItem>

        {/* --- Top Right: News + Points + Streaks --- */}
        <GridItem area="news">
          <RightColumnWrapper>
            <div>
              <SectionHeader>
                <span>Latest News</span>
              </SectionHeader>
              <LatestNews />
            </div>
            <div style={{ marginTop: "auto" }}>
              <PointsBalance />
            </div>
          </RightColumnWrapper>
        </GridItem>

        {/* --- Middle Row: Goals --- */}
        <GridItem area="goals">
          <SectionHeader>
            <span>Goals Overview</span>
            <InfoIcon type="image" content="" imageSrc={GoalsImage} />
          </SectionHeader>
          <GoalsOverview
            goalSource={overviewGoalSource}
            orgMetrics={orgHealthMetrics.slice(-1)}
            selfMetrics={selfHealthMetrics.slice(-1)}
            onGoalToggle={setOverviewGoalSource}
          />
        </GridItem>

        {/* --- Bottom Row: Challenges --- */}
        <GridItem area="challenges">
          <SectionHeader>
            <span>My Active Challenges</span>
            <InfoIcon
              type="tooltip"
              content="This section displays all the challenges you've joined"
            />
          </SectionHeader>
          {/* Pass the data with the processed .image (blob url) property.
            Challanges.tsx uses: src={challenge.image || threadmill} 
          */}
          <Challenges
            challengeData={challengesData}
            loading={challengesLoading}
          />
        </GridItem>
      </DashboardGrid>
    </PageContainer>
  );
};

// --- Sub-Component for Clean Headers ---
const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <HeaderWrapper>{children}</HeaderWrapper>
);

// --- Modern Info Icon Component ---
type InfoIconProps = {
  type: "tooltip" | "image";
  content: string;
  imageSrc?: string;
};

const InfoIcon: React.FC<InfoIconProps> = ({ type, content, imageSrc }) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  const handleClick = () => {
    if (type === "tooltip") {
      setTooltipVisible(!tooltipVisible);
    } else if (type === "image") {
      setModalVisible(true);
    }
  };

  return (
    <>
      <IconWrapper
        onClick={handleClick}
        onMouseEnter={() => type === "tooltip" && setTooltipVisible(true)}
        onMouseLeave={() => type === "tooltip" && setTooltipVisible(false)}
      >
        <Info size={18} />
        {type === "tooltip" && tooltipVisible && <Tooltip>{content}</Tooltip>}
      </IconWrapper>

      {type === "image" && (
        <Modal
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
          width={800}
          centered
        >
          <ModalContent>
            <img
              src={imageSrc}
              alt="Goals Overview"
              style={{ width: "100%", height: "auto" }}
            />
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default HomePage;

// --- STYLED COMPONENTS ---

const PageContainer = styled.div`
  background-color: var(--page-color, #f4f6f8);
  min-height: 100vh;
`;

const DashboardGrid = styled.div`
  display: grid;
  box-sizing: border-box;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;

  padding: 10px;
  gap: 12px;

  /* --- MOBILE LAYOUT --- */
  grid-template-areas:
    "stats"
    "news"
    "goals"
    "challenges";

  grid-template-columns: 1fr;

  /* --- DESKTOP LAYOUT --- */
  @media (min-width: 1024px) {
    padding: 24px;
    gap: 24px;

    grid-template-areas:
      "stats news"
      "goals goals"
      "challenges challenges";

    grid-template-columns: 1fr 1fr;
    align-items: stretch;
  }
`;

const GridItem = styled.div<{ area: string }>`
  grid-area: ${(props) => props.area};
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
  overflow: hidden;
`;

const ActivityChartWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  & > div {
    height: 100% !important;
    min-height: 0;
  }
`;

const RightColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 20px;

  & > div:first-child {
    flex: 1;
    display: flex;
    flex-direction: column;

    & > div {
      height: 100%;
    }
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-color, #333);
`;

const IconWrapper = styled.div`
  position: relative;
  cursor: pointer;
  color: #888;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;

  &:hover {
    color: #007bff;
    background-color: rgba(0, 123, 255, 0.08);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const Tooltip = styled.div`
  position: absolute;
  top: 80%;
  right: calc(100% + 12px);
  transform: translateY(-50%);
  background: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(8px);
  color: white;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.2;
  white-space: normal;
  width: 280px;
  pointer-events: none;
  z-index: 1000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  animation: tooltipFadeIn 0.2s ease-out;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    right: -5px;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-left: 6px solid rgba(15, 23, 42, 0.95);
  }

  @keyframes tooltipFadeIn {
    from {
      opacity: 0;
      transform: translateY(-50%) translateX(5px);
    }
    to {
      opacity: 1;
      transform: translateY(-50%) translateX(0);
    }
  }

  @media (max-width: 768px) {
    width: 240px;
    font-size: 11px;
    padding: 8px 12px;
  }
`;

const ModalContent = styled.div`
  padding: 10px 0;

  img {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;